// src/panels/PanelMemories.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SectionHeader, MemoryChip,isDeadlinePassed } from '../components/SharedComponents';
import { MEMORY_PROMPTS } from '../data/constants';
import useToast from '../hooks/useToast';

function unwrapErrorDetail(errorOrResponse) {
  const raw = errorOrResponse?.response?.data || errorOrResponse?.data || errorOrResponse || {};
  return raw?.detail && typeof raw.detail === 'object' ? raw.detail : raw;
}

function isMemoryLimitError(errorOrResponse) {
  const detail = unwrapErrorDetail(errorOrResponse);
  const code = String(detail?.code || errorOrResponse?.code || '').toUpperCase();
  const limitKey = String(detail?.limitKey || detail?.limit_key || '').toLowerCase();
  const source = String(detail?.source || '').toLowerCase();
  const message = String(detail?.message || errorOrResponse?.message || '').toLowerCase();

  return (
    code === 'MEMORY_LIMIT_EXCEEDED' ||
    code === 'MEMORIES_LIMIT_EXCEEDED' ||
    (code === 'LIMIT_EXCEEDED' && ['memories', 'memory'].includes(limitKey)) ||
    source === 'memories' ||
    message.includes('memory limit') ||
    message.includes('memories limit')
  );
}

function getMemoryText(memory) {
  return memory?.data?.text || memory?.text || '';
}

function isCustomMemory(memory, options) {
  return Boolean(
    memory?.data?.freeText ||
      memory?.data?.free_text ||
      memory?.data?.is_custom ||
      memory?.data?.custom ||
      memory?.freeText ||
      memory?.free_text ||
      options.length === 0
  );
}

export default function PanelMemories({
  memories = [],
  onUpdateMemory,
  onAddCustom,
  onDeleteMemory,
  limit,
  filled = 0,
  onUpgrade,
  bookConfig,
  childName = 'your child',
}) {
  const toast = useToast();
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [cTitle, setCTitle] = useState('');
  const [cVal, setCVal] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [isAddingCustom, setIsAddingCustom] = useState(false);

const isViewOnly = useMemo(
  () => isDeadlinePassed(bookConfig?.content_deadline),
  [bookConfig?.content_deadline]
);

  const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : null;
  const safeFilled = Math.max(0, Number(filled) || 0);

  const getSelectedArray = (text) => {
    if (!text) return [];
    return text.split(', ').filter(Boolean);
  };

  const openMemoryLimitUpgrade = (errorOrResponse) => {
    const detail = unwrapErrorDetail(errorOrResponse);

    onUpgrade?.({
      source: 'memories',
      reason: 'memory_limit',
      currentLimit: detail?.limit || safeLimit,
      current: detail?.current ?? filled,
      remaining: detail?.remaining,
      message: detail?.message,
      upgrade: detail?.upgrade,
      available_offers: detail?.upgrade?.available_offers || detail?.available_offers,
    });
  };

  const handleBackendLimitOrToast = (result, fallbackMessage) => {
    const error = result?.error || result;

    if (isMemoryLimitError(error)) {
      openMemoryLimitUpgrade(error);
      return;
    }

    toast.error(fallbackMessage);
  };

  const saveMemoryValue = async (id, nextValue, successMessage = 'Memory updated.') => {
    if (isViewOnly || savingId) return;

    setSavingId(id);

    try {
      const result = await onUpdateMemory(id, nextValue);

      if (result?.ok) {
        toast.success(successMessage, { autoHideDuration: 1600, replace: true });
      } else {
        handleBackendLimitOrToast(result, 'Could not save memory. Please try again.');
      }
    } catch (err) {
      console.error('Could not save memory:', err);

      if (isMemoryLimitError(err)) {
        openMemoryLimitUpgrade(err);
      } else {
        toast.error('Could not save memory. Please try again.');
      }
    } finally {
      setSavingId(null);
    }
  };

  const toggleSelection = async (id, value) => {
    const memory = memories.find((item) => item.id === id);
    if (!memory) return;

    const currentSelections = getSelectedArray(getMemoryText(memory));
    const isSelected = currentSelections.includes(value);

    const nextSelections = isSelected
      ? currentSelections.filter((item) => item !== value)
      : [...currentSelections, value];

    await saveMemoryValue(id, nextSelections.join(', '));
  };

  const handleAddCustom = async () => {
    if (isViewOnly || isAddingCustom) return;
    if (!cTitle.trim() || !cVal.trim()) return;

    setIsAddingCustom(true);

    try {
      const result = await onAddCustom(cTitle.trim(), cVal.trim());

      if (result?.ok) {
        toast.success('Memory added.');
        setCTitle('');
        setCVal('');
        setCustomMode(false);
      } else {
        handleBackendLimitOrToast(result, 'Could not add memory. Please try again.');
      }
    } catch (err) {
      console.error('Could not add memory:', err);

      if (isMemoryLimitError(err)) {
        openMemoryLimitUpgrade(err);
      } else {
        toast.error('Could not add memory. Please try again.');
      }
    } finally {
      setIsAddingCustom(false);
    }
  };

  const getOptions = (memory) => {
      const backendOptions = memory?.data?.options;

      if (Array.isArray(backendOptions) && backendOptions.length > 0) {
        return backendOptions;
      }

      const title = memory?.data?.title || memory?.title || '';
      const prompt = MEMORY_PROMPTS.find(
        (item) => item.title?.toLowerCase() === title.toLowerCase()
      );

      return Array.isArray(prompt?.options) ? prompt.options : [];
    };

  return (
    <Box>
      <SectionHeader
        title="Growing up memories"
        badge={safeLimit ? `${safeFilled}/${safeLimit} added` : `${safeFilled} added`}
        subtitle={
          isViewOnly ? (
            'The deadline has passed. These memories are locked for printing.'
          ) : (
            <>
              Capture the precious growing-up memories that show who{' '}
              <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>
                {childName}
              </Box>
              {' '}is right now.
            </>
          )
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.25, sm: 1.5 } }}>
        {memories.map((memory) => {
          const title = memory.data?.title || memory.title || 'Memory';
          const memoryText = getMemoryText(memory);
          const options = getOptions(memory);
          const customMemory = isCustomMemory(memory, options);
          const selectedItems = getSelectedArray(memoryText);
          const isSavingThis = savingId === memory.id;
          const isEditingThis = editId === memory.id;

          return (
            <Box
              key={memory.id}
              sx={{
                opacity: isSavingThis ? 0.58 : 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                px: { xs: 2, sm: 2.35 },
                py: { xs: 1.8, sm: 2 },
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 850, letterSpacing: '-0.18px', mb: customMemory ? 0.8 : 1.35 }}>
                    {title}
                  </Typography>

                  {customMemory ? (
                    isEditingThis ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
                          gap: 1,
                          maxWidth: 640,
                        }}
                      >
                        <TextField
                          autoFocus
                          size="small"
                          fullWidth
                          value={draft}
                          onChange={(event) => setDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && draft.trim()) {
                              saveMemoryValue(memory.id, draft.trim());
                              setEditId(null);
                            }
                          }}
                          placeholder="Answer"
                          sx={softInputSx}
                        />
                        <Button
                          variant="contained"
                          disabled={!draft.trim() || isSavingThis}
                          onClick={() => {
                            if (draft.trim()) saveMemoryValue(memory.id, draft.trim());
                            setEditId(null);
                          }}
                          sx={{ borderRadius: '8px', fontWeight: 800 }}
                        >
                          Save
                        </Button>
                        <IconButton onClick={() => setEditId(null)} aria-label="Cancel edit">
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 15,
                          lineHeight: 1.55,
                          color: memoryText.trim() ? 'text.secondary' : 'text.disabled',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {memoryText.trim() || 'No answer yet.'}
                      </Typography>
                    )
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9 }}>
                        {options.map((option) => (
                          <MemoryChip
                            key={option}
                            label={option}
                            active={selectedItems.includes(option)}
                            onClick={() => toggleSelection(memory.id, option)}
                          />
                        ))}

                        {selectedItems
                          .filter((item) => !options.includes(item))
                          .map((customValue) => (
                            <MemoryChip
                              key={customValue}
                              label={customValue}
                              active
                              onClick={() => toggleSelection(memory.id, customValue)}
                            />
                          ))}
                      </Box>

                      {!isViewOnly && (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => {
                            setEditId(memory.id);
                            setDraft('');
                          }}
                          sx={addOwnButtonSx}
                        >
                          + Add your own
                        </Box>
                      )}
                    </Box>
                  )}

                  {!customMemory && isEditingThis && (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
                        gap: 1,
                        maxWidth: 640,
                      }}
                    >
                      <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && draft.trim()) {
                            toggleSelection(memory.id, draft.trim());
                            setEditId(null);
                          }
                        }}
                        placeholder="Type your answer"
                        sx={softInputSx}
                      />
                      <Button
                        variant="contained"
                        disabled={!draft.trim() || isSavingThis}
                        onClick={() => {
                          if (draft.trim()) toggleSelection(memory.id, draft.trim());
                          setEditId(null);
                        }}
                        sx={{ borderRadius: '8px', fontWeight: 800 }}
                      >
                        Add
                      </Button>
                      <IconButton onClick={() => setEditId(null)} aria-label="Cancel custom answer">
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {!isViewOnly && (
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    {customMemory && !isEditingThis && (
                      <IconButton
                        size="small"
                        aria-label={`Edit ${title}`}
                        disabled={Boolean(savingId)}
                        onClick={() => {
                          setEditId(memory.id);
                          setDraft(memoryText);
                        }}
                        sx={{ color: 'grey.500', '&:hover': { color: 'text.primary', bgcolor: 'transparent' } }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}

                    <IconButton
                      size="small"
                      aria-label={`Delete ${title}`}
                      disabled={Boolean(savingId)}
                      onClick={async () => {
                        const result = await onDeleteMemory(memory.id);
                        if (result?.ok) {
                          toast.success('Memory deleted.');
                        } else {
                          toast.error('Could not delete memory. Please try again.');
                        }
                      }}
                      sx={{ color: 'grey.400', '&:hover': { color: 'error.main', bgcolor: 'transparent' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>

      {!isViewOnly && (
        <Box sx={{ mt: 3 }}>
          {customMode ? (
            <Box
              sx={{
                p: { xs: 2, sm: 2.35 },
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ fontSize: 17, fontWeight: 850, mb: 2 }}>Add your own memory</Typography>
              <Stack spacing={1.25}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Prompt, e.g. Favorite superhero"
                  value={cTitle}
                  onChange={(event) => setCTitle(event.target.value)}
                  sx={softInputSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Answer"
                  value={cVal}
                  onChange={(event) => setCVal(event.target.value)}
                  sx={softInputSx}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <Button
                    color="inherit"
                    onClick={() => setCustomMode(false)}
                    disabled={isAddingCustom}
                    sx={{ fontWeight: 800 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAddCustom}
                    disabled={isAddingCustom || !cTitle.trim() || !cVal.trim()}
                    sx={{ borderRadius: '8px', fontWeight: 800 }}
                  >
                    {isAddingCustom ? 'Saving...' : 'Save memory'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Button
              variant="text"
              onClick={() => setCustomMode(true)}
              startIcon={<AddIcon />}
              sx={{ px: 0, mt: 0.5, color: 'text.primary', fontWeight: 850, '&:hover': { bgcolor: 'transparent', color: 'primary.main' } }}
            >
              Add your own memory
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

const addOwnButtonSx = {
  mt: 1.35,
  display: 'inline-flex',
  alignItems: 'center',
  px: 0,
  py: 0.4,
  border: 0,
  bgcolor: 'transparent',
  color: 'text.primary',
  fontSize: 14,
  fontWeight: 800,
  cursor: 'pointer',
  font: 'inherit',
  '&:hover': { color: 'primary.main' },
};

const softInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: 'background.paper',
    '& fieldset': { borderColor: 'grey.200' },
    '&:hover fieldset': { borderColor: 'grey.300' },
    '&.Mui-focused fieldset': { borderColor: 'grey.700', borderWidth: 1 },
  },
};